import { useEffect, useRef } from 'preact/hooks';
import type { Signal } from '@preact/signals';
import type { LeafDto } from '../api/types';
import { getConnectionState } from '../realtime/hubClient';
import type { LeafValue } from '../realtime/signalStore';
import { classifyFreshness, freshnessLabelDe } from '../realtime/freshness';
import { describeStalePolicyDe, staleAfterMsFor } from '../hmi/staleness';

/** "16.10.2026, 12:07:31 (vor 12 s)": ortsuebliche Zeit statt ISO-Zeitstempel,
 *  dazu das Alter, damit man "Stale" einordnen kann (Patrick, 16.10.2026). */
function formatWhenDe(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const when = new Date(t).toLocaleString('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  const age = s < 60 ? `${s} s`
    : s < 3600 ? `${Math.round(s / 60)} min`
    : s < 86400 ? `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`
    : `${Math.floor(s / 86400)} Tagen`;
  return `${when} (vor ${age})`;
}
import { reportValue, writeSignalFor } from '../realtime/writeStore';
import type { SchemaViewSettings } from '../hooks/useSchemaViewSettings';
import { coerceBool, formatLeafValue, formatNumber, inferDecimals, isNumericType } from '../format/leafValue';

interface Props {
  leaf: LeafDto;
  valueSignal: Signal<LeafValue | null> | null;
  /** Optional per-plant hydration flag. Until it flips true and the value is
   *  still null, we render a spinner instead of an em-dash — the schema page
   *  has kicked off a snapshot fetch and the value is imminent. Once
   *  hydration is done, missing values fall back to '—' (still not seen). */
  hydrationSignal?: Signal<boolean> | null;
  /** Ring-buffered recent numeric samples for the sparkline. Read-only. */
  history?: readonly number[] | null;
  /** View toggles from the toolbar — decides what renders around the value. */
  settings: SchemaViewSettings;
}

/**
 * Renders one leaf's current live value + optional overlays (sparkline,
 * min/max bar, unit, change-shine). Reads valueSignal.value and
 * connectionStateSignal.value directly so Preact/Signals re-runs only THIS
 * component on updates — the parent NodeRow and sibling leaves stay put.
 * That's the reason a 5000-leaf tree can afford live updates at all.
 */
export function LeafValueDisplay({
  leaf, valueSignal, hydrationSignal, history, settings,
}: Props) {
  const value = valueSignal ? valueSignal.value : null;
  const connection = getConnectionState().value;
  const freshness = classifyFreshness(value, connection, Date.now(), staleAfterMsFor(leaf));
  // Write-Store per-leaf-Signal: hält Stage (sending/acked/confirmed/failed/
  // readback-timeout) und den vom Operator gesendeten Wert. reportValue()
  // wird bei jedem frischen Value gefeuert damit der Store den Readback-
  // Match erkennt. Der Signal-Read hier subscribed nur diese Zeile — kein
  // Fan-out auf Nachbar-Rows.
  const writeState = writeSignalFor(leaf.id).value;
  useEffect(() => {
    if (value?.v !== undefined) reportValue(leaf.id, value.v);
  }, [value?.v, leaf.id]);
  const hydrated = hydrationSignal ? hydrationSignal.value : true;
  const spinning = value === null && !hydrated;
  const numericValue = typeof value?.v === 'number' && Number.isFinite(value.v) ? value.v : null;

  // Bool leaves get a chip instead of a plain reading. Die Farbe folgt der
  // Bedeutung des Punkts, nicht dem Wert (23.10.2026): ein Alarmkanal ist
  // aktiv rot und in Ruhe neutral, ein Status ist "Ein" in der Messwertfarbe
  // und "Aus" neutral. Rot bleibt Fehlern vorbehalten, wie auf der Karte.
  const isBoolLeaf = leaf.valueType === 'Bool' || leaf.valueType === 'Boolean';
  const boolState = isBoolLeaf && value ? coerceBool(value.v) : null;
  const boolChip = boolState === null ? null : boolChipFor(leaf, boolState);

  const rawFormatted = formatLeafValue(value?.v, leaf);
  // Append the unit inline so the value column reads "42.3 °C" as one atom.
  // Skip for non-numeric types where a unit doesn't make sense, and skip
  // entirely when the operator turned units off in the view settings.
  const showUnit = settings.showUnit && value !== null && leaf.unit && isNumericType(leaf.valueType);
  const formatted = showUnit ? `${rawFormatted} ${leaf.unit}` : rawFormatted;
  const tooltip = value
    ? `${freshnessLabelDe(freshness.label)}: ${formatted}\nGemessen ${formatWhenDe(value.t)}\n${describeStalePolicyDe(leaf)}`
    : spinning
      ? 'Werte werden geladen …'
      : freshnessLabelDe(freshness.label);

  // Change-flash: whenever the numeric/string value actually changes, restart
  // the shine keyframes on the reading element. Compared against the previous
  // value stored in a ref — first render / initial value never flashes. The
  // remove+reflow+add trick makes the CSS animation restart reliably.
  const readingRef = useRef<HTMLSpanElement>(null);
  const prevRef = useRef<unknown>(undefined);
  useEffect(() => {
    if (!settings.flashOnChange) return;
    const v = value?.v;
    const prev = prevRef.current;
    const el = readingRef.current;
    if (el && prev !== undefined && !valuesEqual(prev, v)) {
      el.classList.remove('hv-leaf__reading--flash');
      void el.offsetWidth;
      el.classList.add('hv-leaf__reading--flash');
    }
    prevRef.current = v;
  }, [value?.v, settings.flashOnChange]);

  const writeClass = writeState ? ` hv-leaf__reading-cluster--write-${writeState.stage}` : '';
  return (
    <span class="hv-leaf__value" title={tooltip}>
      {settings.sparkline && (
        <Sparkline history={history ?? null} leaf={leaf} />
      )}
      {settings.minMaxBar && (
        <MinMaxBar value={numericValue} leaf={leaf} />
      )}
      {spinning ? (
        <span class="hv-leaf__spinner" aria-label="Snapshot wird geladen" />
      ) : (
        <span
          class={`hv-leaf__dot hv-leaf__dot--${freshness.tone}`}
          aria-label={freshnessLabelDe(freshness.label)}
        />
      )}
      <span class={`hv-leaf__reading-cluster${writeClass}`}>
      <span ref={readingRef} class={`hv-leaf__reading${value ? '' : ' muted'}${writeState ? ' hv-leaf__reading--pending' : ''}`}>
        {spinning
          ? '…'
          : isBoolLeaf && boolState !== null
            ? (
              <span
                class={`hv-leaf__bool hv-leaf__bool--${boolChip!.tone}`}
                aria-label={boolChip!.label}
              >
                {boolChip!.tone === 'on' && <span class="hv-leaf__bool-dot" aria-hidden="true" />}
                {boolChip!.label}
              </span>
            )
            : formatted}
      </span>
      {writeState && (writeState.stage === 'sending' || writeState.stage === 'acked') && (
        <span class="hv-leaf__write-arrow"
              title={writeState.stage === 'sending'
                ? 'Kommando unterwegs'
                : 'Am Regler akzeptiert, warte auf Readback'}>
          <span class="hv-leaf__write-arrow-tick">→</span> {formatLeafValue(writeState.sentValue, leaf)}
        </span>
      )}
      {writeState && writeState.stage === 'mismatched' && (
        <span class="hv-leaf__write-arrow"
              title={`Regler haelt weiterhin ${formatLeafValue(writeState.actualValue, leaf)} statt ${formatLeafValue(writeState.sentValue, leaf)}`}>
          <span class="hv-leaf__write-arrow-tick">⊘</span> {formatLeafValue(writeState.sentValue, leaf)}
        </span>
      )}
      </span>
    </span>
  );
}

/**
 * Sparkline — a tiny inline SVG polyline of the last N samples for this leaf.
 * Kept intentionally spartan: no axes, no tooltip, no gradients. The stroke
 * colour reflects the current freshness / warn state so the sparkline is
 * legible without a legend. Renders nothing until at least 2 samples are in
 * the ring buffer (a single dot is more noise than signal).
 */
function Sparkline({ history, leaf }: { history: readonly number[] | null; leaf: LeafDto }) {
  if (!history || history.length < 2) {
    return <span class="hv-leaf__spark hv-leaf__spark--empty" aria-hidden="true" />;
  }

  const width = 56;
  const height = 14;
  const padY = 2;

  let min = Infinity;
  let max = -Infinity;
  for (const v of history) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  // Flat line if all values equal — force a small vertical span so it doesn't
  // render at zero-height and disappear.
  if (max - min < 1e-9) {
    min -= 0.5;
    max += 0.5;
  }
  const span = max - min;
  const n = history.length;

  // Bezier-smoothed path: each segment is a quadratic curve whose control
  // point is the "sharp" corner between two raw points, so the drawn curve
  // passes through the midpoints — a cheap monotone smoothing that avoids
  // the zig-zag look of a straight polyline. First point sits at (M x0 y0),
  // then each subsequent point becomes (Q prev, mid).
  const px = (i: number) => (i / (n - 1)) * (width - 2) + 1;
  const py = (i: number) => padY + (1 - (history[i] - min) / span) * (height - 2 * padY);
  let d = `M${px(0).toFixed(1)},${py(0).toFixed(1)} `;
  for (let i = 1; i < n; i++) {
    const cx = px(i - 1);
    const cy = py(i - 1);
    const mx = (cx + px(i)) / 2;
    const my = (cy + py(i)) / 2;
    d += `Q${cx.toFixed(1)},${cy.toFixed(1)} ${mx.toFixed(1)},${my.toFixed(1)} `;
  }
  d += `L${px(n - 1).toFixed(1)},${py(n - 1).toFixed(1)}`;

  // Warn/alarm colour hint when the current sample sits outside the warn
  // band — a subtle visual cue that the sparkline itself is telling something.
  const current = history[n - 1];
  const inWarn = (leaf.warnHigh != null && current > leaf.warnHigh)
              || (leaf.warnLow != null && current < leaf.warnLow);
  const inAlarm = (leaf.alarmHigh != null && current > leaf.alarmHigh)
               || (leaf.alarmLow != null && current < leaf.alarmLow);
  const strokeClass = inAlarm ? 'hv-leaf__spark--alarm'
                    : inWarn ? 'hv-leaf__spark--warn'
                    : '';

  return (
    <svg
      class={`hv-leaf__spark ${strokeClass}`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      <path d={d.trim()} fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" />
    </svg>
  );
}

/**
 * MinMaxBar — a tiny horizontal bar showing where the current value sits
 * within the declared [MinValue, MaxValue] range. When either endpoint is
 * missing, or the value is non-numeric, we render an empty placeholder so
 * neighbouring rows stay column-aligned.
 */
function MinMaxBar({ value, leaf }: { value: number | null; leaf: LeafDto }) {
  const lo = leaf.minValue;
  const hi = leaf.maxValue;

  // Eligibility rules:
  //   1. Must be a numeric value type (bool/enum/flags/string bars are nonsense).
  //   2. Must have both MinValue and MaxValue declared, in a sane order.
  //   3. Must have at least one warn or alarm threshold — otherwise there is
  //      no "band" to visualise, and a raw min/max scale on e.g. a counter
  //      (0..99999999) is more distracting than useful.
  const hasThreshold = leaf.warnLow != null || leaf.warnHigh != null
                    || leaf.alarmLow != null || leaf.alarmHigh != null;
  if (!isNumericType(leaf.valueType) || lo == null || hi == null || hi <= lo || !hasThreshold || value == null) {
    // Placeholder children fill the same grid columns as a full cluster so
    // the slot keeps its 108 px width and rows stay aligned.
    return (
      <span class="hv-leaf__mmb-cluster hv-leaf__mmb-cluster--empty" aria-hidden="true">
        <span class="hv-leaf__mmb-label" />
        <span class="hv-leaf__mmb" />
        <span class="hv-leaf__mmb-label" />
      </span>
    );
  }

  const toPct = (v: number) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
  const clamped = Math.max(lo, Math.min(hi, value));
  const pct = toPct(clamped);

  // Build the coloured axis in strict left-to-right order. Any threshold not
  // declared simply extends the neighbouring zone. Zones with zero or
  // negative width are dropped so the DOM stays minimal.
  const zones: { from: number; to: number; tone: 'ok' | 'warn' | 'error' }[] = [];
  const push = (from: number, to: number, tone: 'ok' | 'warn' | 'error') => {
    if (to > from + 0.05) zones.push({ from, to, tone });
  };
  const alarmLoPct = leaf.alarmLow != null ? toPct(leaf.alarmLow) : null;
  const warnLoPct  = leaf.warnLow  != null ? toPct(leaf.warnLow)  : null;
  const warnHiPct  = leaf.warnHigh != null ? toPct(leaf.warnHigh) : null;
  const alarmHiPct = leaf.alarmHigh != null ? toPct(leaf.alarmHigh) : null;

  // Left edge → alarm-low: red (well outside).
  const leftAlarm = alarmLoPct ?? warnLoPct ?? 0;
  push(0, leftAlarm, alarmLoPct != null ? 'error' : (warnLoPct != null ? 'warn' : 'ok'));
  // Alarm-low → warn-low: orange (warn zone below).
  if (alarmLoPct != null && warnLoPct != null && warnLoPct > alarmLoPct) {
    push(alarmLoPct, warnLoPct, 'warn');
  }
  // Warn-low → warn-high: green safe band.
  const safeStart = warnLoPct ?? alarmLoPct ?? 0;
  const safeEnd   = warnHiPct ?? alarmHiPct ?? 100;
  push(safeStart, safeEnd, 'ok');
  // Warn-high → alarm-high: orange (warn zone above).
  if (warnHiPct != null && alarmHiPct != null && alarmHiPct > warnHiPct) {
    push(warnHiPct, alarmHiPct, 'warn');
  }
  // Alarm-high → 100%: red (well outside on the upper side).
  const rightAlarm = alarmHiPct ?? warnHiPct ?? 100;
  push(rightAlarm, 100, alarmHiPct != null ? 'error' : (warnHiPct != null ? 'warn' : 'ok'));

  // Marker tone reflects the zone the current value is in.
  const inAlarm = (alarmLoPct != null && pct < alarmLoPct) || (alarmHiPct != null && pct > alarmHiPct);
  const inWarn  = !inAlarm && ((warnLoPct != null && pct < warnLoPct) || (warnHiPct != null && pct > warnHiPct));
  const markerTone = inAlarm ? 'hv-leaf__mmb-mark--alarm' : inWarn ? 'hv-leaf__mmb-mark--warn' : '';

  return (
    <span class="hv-leaf__mmb-cluster" aria-hidden="true"
          title={`Bereich ${formatNumberShort(lo)} … ${formatNumberShort(hi)}`}>
      <span class="hv-leaf__mmb-label">{formatNumberShort(lo)}</span>
      <span class="hv-leaf__mmb">
        {zones.map((z) => (
          <span
            class={`hv-leaf__mmb-zone hv-leaf__mmb-zone--${z.tone}`}
            style={{ left: `${z.from}%`, width: `${z.to - z.from}%` }}
          />
        ))}
        <span class={`hv-leaf__mmb-mark ${markerTone}`} style={{ left: `${pct}%` }} />
      </span>
      <span class="hv-leaf__mmb-label">{formatNumberShort(hi)}</span>
    </span>
  );
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') {
    return a === b || (Number.isNaN(a) && Number.isNaN(b));
  }
  return false;
}

function formatNumberShort(n: number): string {
  return Number.isFinite(n) ? formatNumber(n, inferDecimals(n)) : String(n);
}

type BoolTone = 'alarm' | 'quiet' | 'on' | 'off';

/** Text und Ton eines Bool-Chips. Eigene Texte aus den Enum-Eintraegen des
 *  Punkts (0/1, etwa "Zu"/"Offen") gehen vor; die Farbe bleibt die der Rolle. */
function boolChipFor(leaf: LeafDto, on: boolean): { tone: BoolTone; label: string } {
  const entry = leaf.enumEntries?.find((e) => e.value === (on ? 1 : 0));
  const custom = entry ? (entry.displayName || entry.name) : null;
  if (leaf.alarmRole === 'Alarm') {
    return on ? { tone: 'alarm', label: custom ?? 'Aktiv' } : { tone: 'quiet', label: custom ?? 'Normal' };
  }
  return on ? { tone: 'on', label: custom ?? 'Ein' } : { tone: 'off', label: custom ?? 'Aus' };
}
